import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import GooglePlacesAutoCompleteInput from '../../../../../../../../../../../../components/forms/google-places-auto-complete-input/GooglePlacesAutoCompleteInput';
import NumberInput from '../../../../../../../../../../../../components/forms/number-input/NumberInput';
import TextInput from '../../../../../../../../../../../../components/forms/text-input/TextInput';
import ToggleButtonGroup from '../../../../../../../../../../../../components/forms/toggle-button-group/ToggleButtonGroup';
import TooltipIcon from '../../../../../../../../../../../../components/tooltip-icon/TooltipIcon';
import { EMISSION_MAX_FRACTION_DIGIT } from '../../../../../../../../../../../../shared/constants/products.constant';
import { emissionValueWithDecimals } from '../../../../../../../../../../../../shared/helpers/emission-value-with-decimals';
import { getSuffixForNumberInput } from '../../../../../../../../../../../../shared/helpers/textify-numerical-footprint-values-with-their-units';
import { DeclaredUnit } from '../../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../../shared/interfaces/IFootprint';
import RemoveButton from '../../RemoveButton';
import CalculateRouteButton from './calculate-route-button/CalculateRouteButton';
import DetailsDisplayCard from './details-display-card/DetailsDisplayCard';
import { getMainRouteOptions } from './helper/getMainRouteOptions';

interface TransportProcessFormProps {
  name: string;
  processIndex: number;
  onDelete: (showConfirmationDialog: boolean) => void;
}

const TransportProcessForm = ({ name, processIndex, onDelete }: TransportProcessFormProps) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const processes = useWatch({
    name,
    control,
  });

  return (
    <div className='mb-3'>
      <div className='flex flex-row w-full justify-content-between align-items-center pb-4'>
        <h4 className='text-center m-0'>{getValues(`${name}[${processIndex}].name`)}</h4>
        <RemoveButton
          onClick={() => {
            return onDelete(false);
          }}
        />
      </div>
      <div className='mt-4 mb-4 font-light text-black-alpha-60'>
        {t('footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.description')}
      </div>

      <TextInput
        name={`${name}[${processIndex}].route_name`}
        label={t(
          'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.placeholders.routeName',
        )}
        control={control}
      />
      <div className='grid mt-4'>
        <div className='col-6'>
          <GooglePlacesAutoCompleteInput
            name={`${name}[${processIndex}].starting_point`}
            control={control}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.label.startingPoint',
            )}
          />
        </div>
        <div className='col-6'>
          <GooglePlacesAutoCompleteInput
            name={`${name}[${processIndex}].end_location`}
            control={control}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.label.endLocation',
            )}
          />
        </div>
      </div>
      <div className='pb-4'>
        <span className='flex flex-row align-items-baseline gap-1'>
          <p className='text-md font-medium mb-2'>
            {t(
              'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.placeholders.mainRoute',
            )}
          </p>
          <TooltipIcon
            id='main_route'
            tooltipText={t(
              'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.toolTipTexts.mainRoute',
            )}
          />
        </span>

        <ToggleButtonGroup
          name={`${name}[${processIndex}].main_route`}
          control={control}
          buttonGroupDetails={getMainRouteOptions(t)}
        />
      </div>
      {/* Product Weight */}
      <div className='grid'>
        <div className='col-6'>
          <NumberInput
            name={`processes[${processIndex}].product_weight`}
            control={control}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.placeholders.productWeight',
            )}
            suffix=' kg'
            minFractionDigits={0}
            maxFractionDigits={EMISSION_MAX_FRACTION_DIGIT}
            customIcon={
              <TooltipIcon
                id='product_weight'
                tooltipText={t(
                  'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.toolTipTexts.productWeight',
                )}
              />
            }
          />
        </div>
      </div>

      <CalculateRouteButton processIndex={processIndex} processData={processes?.[processIndex]} />

      {processes?.[processIndex]?.route_calculation && (
        <div className='grid'>
          <div className='col-6'>
            <DetailsDisplayCard
              title={t(
                'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.label.distance',
              )}
              content={`${
                processes?.[processIndex]?.route_calculation?.distance &&
                Number(processes?.[processIndex]?.route_calculation?.distance).toFixed(1)
              }`}
              suffix=' km'
            />
          </div>
          <div className='col-6'>
            <DetailsDisplayCard
              title={t(
                'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.label.totalEmissions',
              )}
              content={`${
                processes?.[processIndex]?.route_calculation?.total_emissions &&
                emissionValueWithDecimals(
                  Number(processes?.[processIndex]?.route_calculation?.total_emissions),
                )
              }`}
              customIcon={
                <TooltipIcon
                  id='total_emissions'
                  tooltipText={t(
                    'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.toolTipTexts.totalEmissions',
                  )}
                />
              }
              suffix={getSuffixForNumberInput(DeclaredUnit.piece, 1, EmissionSymbol.KG_CO2E)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportProcessForm;
